/**
 * The one way the apps read GAME_STATE_UPDATED.
 *
 * The server sends each socket the whole game first, then only what changed.
 * Every socket gets one GameStateReceiver that rebuilds the whole game from
 * those messages, for every handler on that socket, so handlers still get the
 * whole game as before. Never listen to GAME_STATE_UPDATED directly: a raw
 * message may be a patch.
 */
import { GameStateReceiver, SocketEvent, } from '@swtorbit/shared-socket-events';
const handlersBySocket = new WeakMap();
/**
 * Set up the socket's receiver the first time it is asked for. It stays for
 * the socket's life, so a handler coming and going (a remount) never loses
 * the state the next patch applies to.
 */
function handlersFor(socket) {
    const existing = handlersBySocket.get(socket);
    if (existing)
        return existing;
    const handlers = new Set();
    const receiver = new GameStateReceiver();
    socket.on(SocketEvent.GAME_STATE_UPDATED, (message) => {
        const receipt = receiver.receive(message);
        if (!receipt)
            return;
        if ('resync' in receipt) {
            socket.emit(SocketEvent.GAME_STATE_RESYNC, receipt.resync);
            return;
        }
        for (const handler of [...handlers])
            handler(receipt.update);
    });
    // A reconnect is a new socket on the server, which starts every game over.
    socket.on('disconnect', () => receiver.reset());
    handlersBySocket.set(socket, handlers);
    return handlers;
}
/** Call `handler` with the whole game on every state update. Returns the unsubscribe. */
export function onGameStateUpdated(socket, handler) {
    const handlers = handlersFor(socket);
    handlers.add(handler);
    return () => {
        handlers.delete(handler);
    };
}
